// CoordinatorPage — dashboard del coordinador.
//
// El coordinador supervisa N supervisores. Esta página lista esos
// supervisores con KPIs agregados de sus equipos. Click en una card
// navega a /supervisor?supervisor=<id> donde puede ver sus choferes
// (mismo dashboard que usa el supervisor para mirar su propia flota).
//
// La data se trae en dos pasos:
//   1. GET /api/coordinator/team → lista de supervisores + warehouses
//   2. Por cada supervisor, GET /api/routes filtrado por sus warehouses
//      + cómputo client-side de KPIs. Paralelo, una request por sup.
//
// No usamos useLiveFleet acá porque solo necesitamos resúmenes (no la
// lista completa de drivers/stops). Cuando el coordinador hace drill-down
// a /supervisor, ahí sí entra useLiveFleet con polling.

import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../data/api";
import { Button, Card } from "../components/ui";
import type { SupervisorTeamMember } from "../data/schemaTypes";

// ---------- helpers de fecha (mismos que SupervisorPage) ----------

function formatLocalDate(d: Date): string {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function dateWindow(plannedDate: string): { from: string; to: string } {
  // ±1 día para absorber timezone — mismo criterio que useLiveFleet.
  const ref = new Date(plannedDate + "T00:00:00Z");
  const prev = new Date(ref.getTime() - 86_400_000);
  const next = new Date(ref.getTime() + 86_400_000);
  const f = (d: Date) => d.toISOString().slice(0, 10);
  return { from: f(prev), to: f(next) };
}

// ---------- KPIs por supervisor ----------

type SupervisorKpis = {
  drivers: number;          // choferes activos con ruta hoy
  stopsTotal: number;
  stopsDone: number;        // delivered + partial
  stopsFailed: number;      // failed + skipped
  efVisita: number;         // (done + failed) / total · 100
};

const EMPTY_KPIS: SupervisorKpis = {
  drivers: 0,
  stopsTotal: 0,
  stopsDone: 0,
  stopsFailed: 0,
  efVisita: 0,
};

/// Computa KPIs agregados a partir de las rutas + stops detalladas del
/// supervisor. Se hace en paralelo (un fetch por ruta).
async function computeSupervisorKpis(
  warehouseIds: string[],
  dateFrom: string,
  dateTo: string,
): Promise<SupervisorKpis> {
  if (warehouseIds.length === 0) return EMPTY_KPIS;
  const routes = await api.listRoutes({
    warehouseIds,
    fromDate: dateFrom,
    toDate: dateTo,
    limit: 200,
  });
  const driverIds = new Set(routes.map((r) => r.driver_user_id).filter(Boolean));
  // Traemos los detalles de cada ruta para sumar stops por status.
  const details = await Promise.all(
    routes.map((r) =>
      api.getRoute(r.id).catch(() => ({ stops: [] as { status: string }[] })),
    ),
  );
  let stopsTotal = 0,
    stopsDone = 0,
    stopsFailed = 0;
  for (const d of details) {
    const stops = d.stops ?? [];
    stopsTotal += stops.length;
    for (const s of stops) {
      if (s.status === "delivered" || s.status === "partial") stopsDone++;
      else if (s.status === "failed" || s.status === "skipped") stopsFailed++;
    }
  }
  const efVisita = stopsTotal === 0 ? 0 : ((stopsDone + stopsFailed) / stopsTotal) * 100;
  return {
    drivers: driverIds.size,
    stopsTotal,
    stopsDone,
    stopsFailed,
    efVisita,
  };
}

// ---------- página ----------

export function CoordinatorPage() {
  const [plannedDate, setPlannedDate] = useState(() => formatLocalDate(new Date()));
  const [team, setTeam] = useState<SupervisorTeamMember[]>([]);
  const [kpisBySupervisor, setKpisBySupervisor] = useState<Map<string, SupervisorKpis>>(
    new Map(),
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Carga del equipo. Solo se hace una vez al montar (el coordinator
  // no cambia mid-session).
  useEffect(() => {
    let cancelled = false;
    api
      .getCoordinatorTeam()
      .then((t) => {
        if (cancelled) return;
        setTeam(t);
      })
      .catch((e) => {
        if (cancelled) return;
        setError((e as Error).message);
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Cálculo de KPIs cuando cambia la fecha o el equipo.
  useEffect(() => {
    if (team.length === 0) return;
    let cancelled = false;
    setLoading(true);
    const { from, to } = dateWindow(plannedDate);
    Promise.all(
      team.map(async (s) => {
        const ids = s.warehouses.map((w) => w.id);
        const kpis = await computeSupervisorKpis(ids, from, to).catch(() => EMPTY_KPIS);
        return [s.user_id, kpis] as const;
      }),
    ).then((entries) => {
      if (cancelled) return;
      setKpisBySupervisor(new Map(entries));
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [team, plannedDate]);

  const totals = useMemo<SupervisorKpis>(() => {
    const acc: SupervisorKpis = { ...EMPTY_KPIS };
    for (const k of kpisBySupervisor.values()) {
      acc.drivers += k.drivers;
      acc.stopsTotal += k.stopsTotal;
      acc.stopsDone += k.stopsDone;
      acc.stopsFailed += k.stopsFailed;
    }
    acc.efVisita =
      acc.stopsTotal === 0
        ? 0
        : ((acc.stopsDone + acc.stopsFailed) / acc.stopsTotal) * 100;
    return acc;
  }, [kpisBySupervisor]);

  if (loading && team.length === 0) {
    return <Centered title="Cargando equipo…" />;
  }
  if (error && team.length === 0) {
    return (
      <Centered title="No se pudo cargar el equipo">
        <p className="text-sm text-sub">{error}</p>
      </Centered>
    );
  }
  if (team.length === 0) {
    return (
      <Centered title="Sin supervisores asignados">
        <p className="text-sm text-sub mb-3">
          Como coordinador no tenés supervisores en tu equipo. Hablá con el
          admin para que te asigne.
        </p>
      </Centered>
    );
  }

  return (
    <div className="flex-1 overflow-auto p-4 space-y-4">
      <header className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-xl font-semibold text-ink">Equipo</h1>
          <p className="text-xs text-sub mt-0.5">
            {team.length} supervisor{team.length === 1 ? "" : "es"} ·{" "}
            {totals.drivers} chofer{totals.drivers === 1 ? "" : "es"} hoy
          </p>
        </div>
        <div className="flex items-center gap-2">
          <input
            type="date"
            value={plannedDate}
            onChange={(e) => setPlannedDate(e.target.value)}
            className="border border-border rounded px-2 py-1.5 text-sm"
          />
        </div>
      </header>

      {/* KPIs agregados del equipo entero */}
      <Card padding="md">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Kpi label="Avance equipo" value={`${totals.efVisita.toFixed(0)}%`} />
          <Kpi label="Choferes activos" value={String(totals.drivers)} />
          <Kpi label="Entregas exitosas" value={String(totals.stopsDone)} unit={`/${totals.stopsTotal}`} />
          <Kpi
            label="Rechazos"
            value={String(totals.stopsFailed)}
            accent={totals.stopsFailed > 0}
          />
        </div>
      </Card>

      {/* Grid de cards por supervisor */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {team.map((s) => (
          <SupervisorCard
            key={s.user_id}
            supervisor={s}
            kpis={kpisBySupervisor.get(s.user_id) ?? EMPTY_KPIS}
            loading={loading && !kpisBySupervisor.has(s.user_id)}
          />
        ))}
      </div>
    </div>
  );
}

// ---------- card por supervisor ----------

function SupervisorCard({
  supervisor: s,
  kpis,
  loading,
}: {
  supervisor: SupervisorTeamMember;
  kpis: SupervisorKpis;
  loading: boolean;
}) {
  const pctClass =
    kpis.efVisita >= 80
      ? "bg-state-delivered/10 text-state-delivered"
      : kpis.efVisita >= 50
        ? "bg-state-delayed/10 text-state-delayed"
        : "bg-state-failed/10 text-state-failed";

  return (
    <Card
      padding="md"
      className={kpis.stopsFailed > 0 ? "border-l-4 border-l-state-failed" : ""}
    >
      <div className="flex items-start gap-3">
        {/* Avatar */}
        <div className="w-12 h-12 rounded-full bg-page flex items-center justify-center shrink-0 text-sub">
          <svg viewBox="0 0 24 24" fill="currentColor" className="w-7 h-7" aria-hidden="true">
            <path d="M12 12a4 4 0 1 0-4-4 4 4 0 0 0 4 4Zm0 2c-3.33 0-8 1.67-8 5v1h16v-1c0-3.33-4.67-5-8-5Z" />
          </svg>
        </div>

        {/* Nombre + warehouses */}
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-ink truncate">{s.full_name}</div>
          <div className="text-xs text-sub">Supervisor</div>
          {s.warehouses.length > 0 && (
            <div className="mt-1.5 flex flex-wrap gap-1">
              {s.warehouses.map((w) => (
                <span
                  key={w.id}
                  className="text-[10px] px-1.5 py-0.5 bg-page rounded font-mono text-sub"
                  title={w.name}
                >
                  {w.code || w.name}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Badge de avance */}
        <div className="text-right shrink-0">
          <div className={`px-2.5 py-1 rounded text-sm font-semibold ${pctClass}`}>
            {kpis.efVisita.toFixed(0)}%
          </div>
          <div className="text-[10px] text-sub mt-0.5">Avance</div>
        </div>
      </div>

      {/* Métricas + acción */}
      <div className="mt-3 pt-3 border-t border-border flex items-center justify-between gap-3">
        <div className="flex items-baseline gap-5 text-sm">
          <Metric n={kpis.drivers} label="Choferes" />
          <Metric n={kpis.stopsDone} label="Entregas" />
          <Metric n={kpis.stopsFailed} label="Rechazos" accent={kpis.stopsFailed > 0} />
        </div>
        <Link to={`/supervisor?supervisor=${encodeURIComponent(s.user_id)}`}>
          <Button variant="primary" disabled={loading}>
            Ver flota
          </Button>
        </Link>
      </div>
    </Card>
  );
}

function Metric({
  n,
  label,
  accent,
}: {
  n: number;
  label: string;
  accent?: boolean;
}) {
  return (
    <div className="text-center">
      <div
        className={`text-base font-semibold tabular-nums ${
          accent ? "text-state-failed" : "text-ink"
        }`}
      >
        {n}
      </div>
      <div className="text-[10px] uppercase tracking-wide text-sub">{label}</div>
    </div>
  );
}

function Kpi({
  label,
  value,
  unit,
  accent,
}: {
  label: string;
  value: string;
  unit?: string;
  accent?: boolean;
}) {
  return (
    <div className="text-center">
      <div className="text-[10px] uppercase tracking-wide text-sub">{label}</div>
      <div className={`font-mono ${accent ? "text-state-failed" : "text-ink"}`}>
        <span className="text-xl font-semibold">{value}</span>
        {unit && <span className="text-xs text-sub ml-1 font-normal">{unit}</span>}
      </div>
    </div>
  );
}

function Centered({ title, children }: { title: string; children?: React.ReactNode }) {
  return (
    <div className="flex-1 flex items-center justify-center p-6">
      <Card padding="lg" className="max-w-md text-center">
        <h2 className="text-base text-ink font-semibold mb-2">{title}</h2>
        {children}
      </Card>
    </div>
  );
}
